import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './features/theme/ThemeProvider';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/Confirm';
import { AuthProvider } from './features/auth/AuthProvider';
import { RequireAuth } from './features/auth/RequireAuth';
import { RequireAdmin } from './features/auth/RequireAdmin';

// Auth pages are kept eager — they're tiny and load on first paint anyway.
import { Login } from './routes/login/Login';
import { Register } from './routes/register/Register';
import { VerifyEmail } from './routes/verify-email/VerifyEmail';
import { ResetPassword } from './routes/reset-password/ResetPassword';

// Shells are eager because they wrap every protected route.
import { AppShell } from './layouts/AppShell';
import { AdminLayout } from './layouts/AdminLayout';
import { NotFound } from './routes/not-found/NotFound';

// Surveillance routes — each becomes its own chunk so analysts don't pay
// for the chart-heavy Contract page on the leaderboard.
const TotpSetup = lazy(() => import('./routes/totp-setup/TotpSetup').then((m) => ({ default: m.TotpSetup })));
const Account = lazy(() => import('./routes/account/Account').then((m) => ({ default: m.Account })));
const EventDetail = lazy(() => import('./routes/event-detail/EventDetail').then((m) => ({ default: m.EventDetail })));
const Leaderboard = lazy(() => import('./routes/leaderboard/Leaderboard').then((m) => ({ default: m.Leaderboard })));
const LeaderboardResults = lazy(() => import('./routes/leaderboard/LeaderboardResults').then((m) => ({ default: m.LeaderboardResults })));
const MarketSurveillance = lazy(() => import('./routes/market-surveillance/MarketSurveillance').then((m) => ({ default: m.MarketSurveillance })));
const Contract = lazy(() => import('./routes/contract/Contract').then((m) => ({ default: m.Contract })));
const Wallets = lazy(() => import('./routes/wallets/Wallets').then((m) => ({ default: m.Wallets })));
const Wallet = lazy(() => import('./routes/wallet/Wallet').then((m) => ({ default: m.Wallet })));
const Traced = lazy(() => import('./routes/traced/Traced').then((m) => ({ default: m.Traced })));
const WalletTrace = lazy(() => import('./routes/wallet-trace/WalletTrace').then((m) => ({ default: m.WalletTrace })));
const Watchlists = lazy(() => import('./routes/watchlists/Watchlists').then((m) => ({ default: m.Watchlists })));
const SavedSearches = lazy(() => import('./routes/saved-searches/SavedSearches').then((m) => ({ default: m.SavedSearches })));
const WatchlistDetail = lazy(() => import('./routes/watchlist-detail/WatchlistDetail').then((m) => ({ default: m.WatchlistDetail })));

const AdminUsers = lazy(() => import('./routes/admin/users/AdminUsers').then((m) => ({ default: m.AdminUsers })));
const AdminUserDetail = lazy(() => import('./routes/admin/user-detail/AdminUserDetail').then((m) => ({ default: m.AdminUserDetail })));
const AdminDomains = lazy(() => import('./routes/admin/domains/AdminDomains').then((m) => ({ default: m.AdminDomains })));
const AdminMonitoredTags = lazy(() => import('./routes/admin/monitored-tags/AdminMonitoredTags').then((m) => ({ default: m.AdminMonitoredTags })));
const AdminMonitoredKeywords = lazy(() => import('./routes/admin/monitored-keywords/AdminMonitoredKeywords').then((m) => ({ default: m.AdminMonitoredKeywords })));
const AdminConfig = lazy(() => import('./routes/admin/config/AdminConfig').then((m) => ({ default: m.AdminConfig })));
const AdminAnnouncements = lazy(() => import('./routes/admin/announcements/AdminAnnouncements').then((m) => ({ default: m.AdminAnnouncements })));
const AdminSavedViews = lazy(() => import('./routes/admin/saved-views/AdminSavedViews').then((m) => ({ default: m.AdminSavedViews })));
const AdminMonitoring = lazy(() => import('./routes/admin/monitoring/AdminMonitoring').then((m) => ({ default: m.AdminMonitoring })));
const AdminActivityFeed = lazy(() => import('./routes/admin/activity-feed/AdminActivityFeed').then((m) => ({ default: m.AdminActivityFeed })));
const AdminUserActivity = lazy(() => import('./routes/admin/user-activity/AdminUserActivity').then((m) => ({ default: m.AdminUserActivity })));
const AdminReverseLookup = lazy(() => import('./routes/admin/reverse-lookup/AdminReverseLookup').then((m) => ({ default: m.AdminReverseLookup })));
const AdminAudit = lazy(() => import('./routes/admin/audit/AdminAudit').then((m) => ({ default: m.AdminAudit })));
const AdminTraceRequests = lazy(() => import('./routes/admin/trace-requests/AdminTraceRequests').then((m) => ({ default: m.AdminTraceRequests })));
const AdminSystem = lazy(() => import('./routes/admin/system/AdminSystem').then((m) => ({ default: m.AdminSystem })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

function PageFallback() {
  // Empty fallback by design — pages render their own loading skeletons via
  // TanStack Query's isLoading. A spinner here would create a double-blink.
  return null;
}

// Legacy /contract/:cid → /market/:cid redirect (renamed 2026-06-08).
function LegacyContractRedirect() {
  const { conditionId } = useParams();
  return <Navigate to={`/market/${conditionId}`} replace />;
}

/** `/market-surveillance/results` -> `/event-surveillance/results`, keeping the
 *  query string. `<Navigate>` does not preserve `search` on its own, and the
 *  scope lives entirely in `?events=` / `?markets=` — a redirect that dropped it
 *  would turn a shared link into an empty page. */
function LegacyResultsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/event-surveillance/results${search}`} replace />;
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ConfirmProvider>
            <BrowserRouter>
              <AuthProvider>
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  <Route element={<RequireAuth />}>
                    <Route path="/totp-setup" element={<TotpSetup />} />
                    <Route element={<AppShell />}>
                      <Route path="/" element={<Navigate to="/event-surveillance" replace />} />
                      <Route path="/events/:slug" element={<EventDetail />} />
                      {/* Renamed /leaderboard -> /market-surveillance 2026-08-27,
                          then -> /event-surveillance 2026-09-08. The components
                          keep their Leaderboard names: the page still produces a
                          leaderboard of wallets, "Event surveillance" is the
                          name of the workflow around it. */}
                      <Route path="/event-surveillance" element={<Leaderboard />} />
                      <Route path="/event-surveillance/results" element={<LeaderboardResults />} />
                      {/* /market-surveillance is now its OWN page (2026-09-08):
                          a typeahead that finds one market and opens it. It
                          held a redirect to /event-surveillance for a few hours
                          between the rename and this page landing.

                          THE BOOKMARK CONSEQUENCE, deliberately accepted: this
                          path was the app's landing page for weeks before the
                          rename, so a bookmark from then now lands on market
                          search rather than event surveillance. The path means
                          what it says, and the nav puts both a click away.

                          /market-surveillance/results has no such ambiguity —
                          nothing under this page produces a results URL — so it
                          keeps redirecting. It carries the query string through
                          because `?events=…` IS the scope, and dropping it would
                          land people on an empty results page rather than the
                          one they linked to. */}
                      <Route path="/market-surveillance" element={<MarketSurveillance />} />
                      <Route
                        path="/market-surveillance/results"
                        element={<LegacyResultsRedirect />}
                      />
                      <Route path="/market/:conditionId" element={<Contract />} />
                      {/* Legacy /contract/:cid URLs (pre-2026-06-08) — redirect
                          so old bookmarks and screenshots still resolve. */}
                      <Route path="/contract/:conditionId" element={<LegacyContractRedirect />} />
                      <Route path="/wallets" element={<Wallets />} />
                      <Route path="/wallet/:address" element={<Wallet />} />
                      <Route path="/wallet/:address/trace" element={<WalletTrace />} />
                      <Route path="/watchlists" element={<Watchlists />} />
                      <Route path="/traced" element={<Traced />} />
                      <Route path="/saved-searches" element={<SavedSearches />} />
                      <Route path="/watchlists/:id" element={<WatchlistDetail />} />
                      <Route path="/account" element={<Account />} />

                      <Route element={<RequireAdmin />}>
                        <Route element={<AdminLayout />}>
                          <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
                          <Route path="/admin/users" element={<AdminUsers />} />
                          <Route path="/admin/users/:id" element={<AdminUserDetail />} />
                          <Route path="/admin/domains" element={<AdminDomains />} />
                          <Route path="/admin/monitored-tags" element={<AdminMonitoredTags />} />
                          <Route path="/admin/monitored-keywords" element={<AdminMonitoredKeywords />} />
                          <Route path="/admin/config" element={<AdminConfig />} />
                          <Route path="/admin/announcements" element={<AdminAnnouncements />} />
                          <Route path="/admin/saved-views" element={<AdminSavedViews />} />
                          <Route path="/admin/monitoring" element={<AdminMonitoring />} />
                          <Route path="/admin/activity-feed" element={<AdminActivityFeed />} />
                          <Route path="/admin/monitoring/users/:id" element={<AdminUserActivity />} />
                          <Route path="/admin/reverse-lookup" element={<AdminReverseLookup />} />
                          <Route path="/admin/audit" element={<AdminAudit />} />
                          <Route path="/admin/system" element={<AdminSystem />} />
                          <Route path="/admin/trace-requests" element={<AdminTraceRequests />} />
                        </Route>
                      </Route>
                    </Route>
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </AuthProvider>
            </BrowserRouter>
          </ConfirmProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
